import React, { useState } from "react";
import { Server, Shield, Database, Lock, CheckCircle2, Cpu, Eye, ExternalLink } from "lucide-react";
import "./Projects.css";

const projectsList = [
  {
    id: "rate-limiter",
    title: "Behavior-Based API Rate Limiter",
    subtitle: "Intelligent Distributed Request Throttling Engine",
    organization: "Core Systems Engineering",
    type: "Backend & Infrastructure",
    image: null, // Pure system architecture diagram visual
    badge: "Infrastructure System",
    isPrivate: true,
    points: [
      "Engineered an intelligent API rate-limiting system in Node.js with Redis for request tracking.",
      "Implemented realtime risk scoring and adaptive throttling using a sliding-window algorithm.",
      "Built fault-tolerant fallback architecture to maintain backend reliability during Redis outages.",
    ],
    tech: ["Node.js", "Redis", "Sliding Window", "Express.js", "Resilience Fallbacks", "System Design"],
    systemStats: [
      { label: "Algorithm", value: "Sliding Window" },
      { label: "Tracking Store", value: "In-Memory Redis" },
      { label: "High Availability", value: "Graceful Fallback" },
    ],
  },
  {
    id: "lms-admin",
    title: "LMS Platform (Admin Dashboard)",
    subtitle: "Educational Management & Analytics Dashboard",
    organization: "Cipheren Technology",
    type: "Full Stack & Enterprise System",
    image: "/admin.jpeg",
    badge: "Enterprise Platform",
    isPrivate: true,
    points: [
      "Architected a full-stack LMS platform with an admin dashboard using React.js and Node.js.",
      "Implemented role-based authentication (RBAC) and realtime monitoring features.",
      "Developed RESTful APIs and scalable backend workflows.",
      "Built analytics modules for educational performance tracking.",
    ],
    tech: ["React.js", "Node.js", "JWT Auth", "RBAC", "RESTful APIs", "Dashboard Analytics"],
    systemStats: [
      { label: "Architecture", value: "Full Stack MVC" },
      { label: "Security", value: "Role-Based Access" },
      { label: "Monitoring", value: "Realtime Analytics" },
    ],
  },
  {
    id: "erp-business",
    title: "ERP System (Business Management)",
    subtitle: "Enterprise Inventory, Billing & Accounts Platform",
    organization: "Graduate Udyog Samuh",
    type: "Enterprise ERP & Database Architecture",
    image: "/erp.jpeg",
    badge: "Production Enterprise ERP",
    isPrivate: true,
    points: [
      "Developed a scalable ERP platform for billing, inventory, and account management.",
      "Automated business workflows and operational reporting.",
      "Optimized backend data processing and PostgreSQL database query handling.",
    ],
    tech: ["Node.js", "PostgreSQL", "Billing Automation", "Inventory Engine", "Relational Modeling", "Query Tuning"],
    systemStats: [
      { label: "Database", value: "PostgreSQL" },
      { label: "Workflows", value: "Automated Billing" },
      { label: "Operations", value: "Multi-Module ERP" },
    ],
  },
  {
    id: "crm-carcuro",
    title: "CRM System (Vehicle Service Operations)",
    subtitle: "Operational CRM & Workflow Scheduling Platform",
    organization: "Car Curo",
    type: "Operations Software & Relational CRM",
    image: "/carcrm.jpeg",
    badge: "Production CRM System",
    isPrivate: true,
    points: [
      "Implemented a customer relationship management platform for a vehicle service business.",
      "Managed booking systems, customer profiles, and vehicle service history through a relational database.",
      "Improved operational coordination and customer support workflows.",
    ],
    tech: ["Node.js", "Relational Database", "RESTful APIs", "Booking Workflows", "Service Tracking"],
    systemStats: [
      { label: "Core Workflow", value: "Service Bookings" },
      { label: "Data Integrity", value: "Relational History" },
      { label: "Coordination", value: "Staff Allocation" },
    ],
  },
  {
    id: "portfolio-web",
    title: "Personal Portfolio Website",
    subtitle: "Bespoke Editorial Engineering Website",
    organization: "premgore.in",
    type: "Frontend Engineering & Web Platform",
    image: null,
    badge: "Live Portfolio",
    isPrivate: false,
    url: "https://premgore.in",
    points: [
      "Built a responsive full-stack portfolio website using React.js.",
      "Optimized frontend performance, editorial typography, and deployment workflows.",
      "Configured Vercel deployment with custom domain and SSL integration.",
    ],
    tech: ["React.js", "TypeScript", "Modern CSS", "Vercel", "Domain & SSL", "Performance Tuning"],
    systemStats: [
      { label: "Framework", value: "React.js" },
      { label: "Hosting", value: "Vercel Edge" },
      { label: "Domain", value: "premgore.in" },
    ],
  },
];

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);

  return (
    <section id="work" className="section projects-section">
      <div className="container">
        
        {/* SECTION HEADER */}
        <div className="section-header">
          <div className="eyebrow">01 / Featured Engineering</div>
          <h2 className="section-title">
            Selected <em>Engineering Systems</em>
          </h2>
          <p className="section-subtitle">
            Core infrastructure, enterprise business platforms (ERP/CRM/LMS), and distributed backend architectures designed for operational reliability.
          </p>
        </div>

        {/* PROJECTS LIST */}
        <div className="projects-editorial-list">
          {projectsList.map((project, index) => (
            <article key={project.id} className="project-editorial-card">
              
              {/* CARD TOP INFO */}
              <div className="card-top-row">
                <div className="project-meta-left">
                  <span className="project-index">0{index + 1}</span>
                  <span className="badge badge-terracotta">{project.badge}</span>
                  <span className="project-org">• {project.organization}</span>
                </div>
                
                {project.isPrivate ? (
                  <div className="private-status-tag" title="Enterprise private production deployment">
                    <Lock size={12} />
                    <span>Private / Production System</span>
                  </div>
                ) : (
                  <a
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="public-link-tag"
                  >
                    <span>Visit Deployment</span>
                    <ExternalLink size={12} />
                  </a>
                )}
              </div>

              {/* CARD MAIN CONTENT GRID */}
              <div className={`project-card-grid ${project.image ? "has-image" : "no-image"}`}>
                
                {/* TEXT & BULLETS */}
                <div className="project-info-column">
                  <h3 className="project-card-title">{project.title}</h3>
                  <div className="project-card-subtitle">{project.subtitle}</div>

                  <ul className="project-bullet-list">
                    {project.points.map((pt, pIdx) => (
                      <li key={pIdx}>
                        <CheckCircle2 size={16} className="bullet-check-icon" />
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>

                  {/* SYSTEM SPECS BAR */}
                  <div className="system-specs-bar">
                    {project.systemStats.map((stat, sIdx) => (
                      <div key={sIdx} className="spec-item">
                        <span className="spec-label">{stat.label}:</span>
                        <strong className="spec-val">{stat.value}</strong>
                      </div>
                    ))}
                  </div>

                  {/* TECH STACK */}
                  <div className="project-tech-pills">
                    {project.tech.map((t, tIdx) => (
                      <span key={tIdx} className="tech-pill">{t}</span>
                    ))}
                  </div>
                </div>

                {/* VISUAL COLUMN (PRESERVED PROJECT ASSETS) */}
                {project.image ? (
                  <div className="project-image-column">
                    <div className="project-img-frame">
                      <img
                        src={project.image}
                        alt={`${project.title} Interface`}
                        className="project-screenshot"
                      />
                      <div className="img-caption-bar">
                        <span>Production Interface Snapshot</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="project-infra-column">
                    <div className="infra-abstract-card">
                      <div className="infra-header">
                        <Server size={18} className="infra-icon" />
                        <span>Architectural Blueprint</span>
                      </div>
                      <div className="infra-diagram-content">
                        <div className="infra-node root">
                          <span>Incoming API Traffic</span>
                        </div>
                        <div className="infra-arrow">↓</div>
                        <div className="infra-node core">
                          <span>Redis Sliding Window + Risk Scorer</span>
                        </div>
                        <div className="infra-arrow">↓</div>
                        <div className="infra-node success">
                          <span>Downstream Express Services</span>
                        </div>
                      </div>
                      <div className="infra-note">
                        Resilient fallback architecture to bypass Redis during service outages.
                      </div>
                    </div>
                  </div>
                )}

              </div>

            </article>
          ))}
        </div>

      </div>
    </section>
  );
};

export default Projects;